<template>
    <div class="pipeline-view__container player-management-view">
        <view-header :title="'Player Management'">
            <template v-slot:viewicon>
                <i class="fa-duotone fa-layer-group"></i>
            </template>
        </view-header>
        <k-grid
            :data-items="filteredItems"
            :data-item-key="'id'"
            :columns="columns"
            :sortable="sortable"
            :sort="sort"
            :filterable="true"
            :filter="filter"
            :group="group"
            :column-menu="true"
            :pageable="gridPageable"
            :take="take"
            :total="total"
            :skip="skip"
            @datastatechange="dataStateChange"
            @rowclick="onRowClick"
            @filterchange="onFilterChange"
            @sortchange="onSortChange">
            <template v-slot:pipelineActions="{ props }">
                <div class="pipeline-actions__container">
                    <k-button class="delete-item__btn" @click.stop="onDeleteItem(props.dataItem)" title="Delete Player"><i class="fa-duotone fa-trash"></i></k-button>
                </div>
            </template>
            <k-grid-toolbar>
                    <span class="k-textbox k-grid-search k-display-flex">
                        <k-input :style="{ width: '300px' }"
                                  placeholder="Search in all columns..."
                                  :value="searchWord"
                                  :icon-name="'search'"
                                  @input="onSearchChange"></k-input>
                    </span>
                    <k-dropdown class="grid-filter__select" 
                                :data-items="filterSelects" 
                                v-model="selectedFilter"></k-dropdown>
                    <div class="grid-button__group">
                        <k-button title="Create Player"
                                    class="create-player__btn"
                                    @click="onCreateItem">
                            <i class="fa-solid fa-hockey-stick-puck"></i> Create Player
                        </k-button>
                        <k-button title="Export to Excel"
                                    class="export-excel__btn"
                                    @click="exportExcel">
                            <i class="fa-solid fa-file-export"></i> Export to Excel
                        </k-button>
                    </div>
            </k-grid-toolbar>
        </k-grid>

        <create-player-dialog :showDialog="showCreateDialog"
                            @dialogclosed="toggleCreateDialog"
                            @playercreated="getItems"></create-player-dialog>

        <confirm-dialog :showDialog="showConfirmDialog"
                        :dialog-Msg="confirmMsg"
                        :icon-class="confirmIconClass"
                        @dialogclosed="toggleConfirmDialog"
                        @cancelclicked="toggleConfirmDialog"
                        @confirmclicked="deleteItem"></confirm-dialog>

        <div class="loader-container" v-if="isLoading">
            <k-loader :size="'large'" :type="'infinite-spinner'"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>

        <k-notification-group class="default-notification-container">
                <k-fade :appear="successNotification">
                    <k-notification v-if="successNotification"
                                    :type="{ style: 'success', icon: true }"
                                    :closable="true"
                                    @close="onCloseNotification('successNotification')">
                        <span>{{ notificationMsg }}</span>
                    </k-notification>
                </k-fade>
        </k-notification-group>
    </div>
</template>

<script>
    import { DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Dialog } from '@progress/kendo-vue-dialogs';
    import { Grid, GridToolbar } from '@progress/kendo-vue-grid';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Input } from "@progress/kendo-vue-inputs";
    import { Button } from "@progress/kendo-vue-buttons";
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade } from "@progress/kendo-vue-animation";
    
    import { saveExcel } from '@progress/kendo-vue-excel-export';
    import { orderBy, filterBy, process } from '@progress/kendo-data-query';

    import viewHeader from '../components/viewHeader.vue';
    import confirmDialog from '../components/confirmDialog.vue';
    import createPlayerDialog from '../components/createPlayerDialog.vue';

    import cloneDeep from 'lodash.clonedeep';

    import auth from '../authConfig';
    import apiService from '../api/api.service.js';

    export default {
        name: 'playerManagementView',
        data() {
            return {
                allItems: [],
                columns: [
                    { field: 'fullName', title: 'Name' },
                    { field: 'status', title: 'Status' },
                    { field: 'nhlRightsTeam', title: 'NHL Rights' },
                    { field: 'dob', title: 'DOB', filter: 'date', type: 'date', format: '{0:M/d/y}' },
                    { field: 'playerAge', title: 'Age', type: 'number', filter: 'numeric' },
                    { field: 'positionString', title: 'Position(s)' },
                    { field: 'contractValueField', title: 'Annual Contract Value', type: 'number', filter: 'numeric', format: '{0:c0}' },
                    { cell: 'pipelineActions', filterable: false, columnMenu: false, width: '100' }
                ],
                confirmIconClass: '',
                confirmMsg: '',
                createTitle: 'Create Player',
                deleteItemId: '',
                filter: { filters: [{ field: 'status', operator: 'contains', value: 'Client'}], logic: 'and' },
                filteredItems: [],
                filterSelects: ['All Players', 'Clients & Targets', 'Clients', 'Targets', 'Former Players', 'Retired'],
                gridPageable: {
                    buttonCount: 5,
                    info: true,
                    type: "numeric",
                    pageSizes: [5, 10, 20, 50],
                    previousNext: true,
                },
                group: [],
                isLoading: false,
                notificationMsg: '',
                searchWord: '',
                selectedFilter: 'Clients',
                showConfirmDialog: false,
                showCreateDialog: false,
                showErrorDialog: false,
                skip: 0,
                sort: [{ field: 'contractValueField', dir: 'desc' }],
                sortable: { allowUnsort: false, mode: 'single' },
                successNotification: false,
                take: 50,
                total: 0
            }
        }, 
        watch: {
            '$store.state.errorAlert'(val) {
                if (val) {
                    this.onRequestError(this.$store.state.errorTitle, this.$store.state.errorMsg);

                    let errorData = {
                        isError: false,
                        title: '',
                        msg: ''
                    }
                    
                    this.$store.commit('updateErrorAlert', errorData);
                }
            },
            selectedFilter(val) {
                let filter;

                switch(val) {
                    case 'All Players':
                        filter = { field: 'status', operator: 'contains', value: '' };
                        break;
                    case 'Clients & Targets':
                        filter = { logic: 'or', filters: [{ field: 'status', operator: 'contains', value: 'Client' }, { field: 'status', operator: 'contains', value: 'Target' }] };
                        break;
                    case 'Former Players':
                        filter = { field: 'status', operator: 'contains', value: 'Former Player' };
                        break;
                    case 'Clients':
                        filter = { field: 'status', operator: 'contains', value: 'Client' };
                        break;
                    case 'Targets':
                        filter = { field: 'status', operator: 'contains', value: 'Target' };
                        break;
                    case 'Retired':
                        filter = { field: 'status', operator: 'contains', value: 'Retired' };
                }

                if (!this.filter) {
                    this.onFilterChange({ filter: { filters: [ filter ], logic: 'and' } });
                } else {
                    let statusFilterIndex = this.getStatusFilterIndex();

                    if (statusFilterIndex >= 0) {
                        this.filter.filters.splice(statusFilterIndex, 1, filter );
                    } else {
                        this.filter.filters.push(filter);
                    }

                    this.onFilterChange({ filter: this.filter });
                }
            }
        },
        mounted() {
            this.getItems();
        },
        methods: {
            createAppState(dataState) {
                this.group = dataState.group;
                this.take = dataState.take;
                this.skip = dataState.skip;
                this.sort = dataState.sort;
                this.filter = dataState.filter;

                this.getData();
            },
            dataStateChange(event) {
                this.createAppState(event.data);
            },
            async deleteItem() {
                let self = this;

                this.isLoading = true;
                this.toggleConfirmDialog();

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.DeletePlayer(tokenResponse.accessToken, this.deleteItemId) 
                    .then(() => {
                        self.getItems(true);
                        self.showNotification('Player deactivated successfully');
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            exportExcel() {
                let data = this.getFilteredItems();

                saveExcel({
                    data: data,
                    fileName: "Players",
                    columns: this.columns
                });
            },
            getData() {
                let filteredData = this.getFilteredItems();
              
                this.filteredItems = process(filteredData, {
                    take: this.take,
                    skip: this.skip,
                    group: this.group,
                    sort: this.sort,
                    filter: this.filter,
                }).data;

                this.total = process(filteredData, {
                    filter: this.filter
                }).total;

                this.isLoading = false;
            },
            getFilteredItems() {
                if (this.searchWord) {
                    this.filteredItems = this.allItems.filter(item => {
                        return Object.values(item).some(val => val && val.toString().toLowerCase().includes(this.searchWord.toLowerCase()));
                    });
                } else {
                    this.filteredItems = cloneDeep(this.allItems);
                }

                if (this.sort.length > 0) {
                    this.filteredItems = orderBy(this.filteredItems, this.sort);
                }

                if (this.filter) {
                    this.filteredItems = filterBy(this.filteredItems, this.filter);

                    let statusFilterIndex = this.getStatusFilterIndex();

                    if (statusFilterIndex === -1) {
                        this.selectedFilter = 'Clients & Targets';
                    } 
                } else {
                    this.selectedFilter = 'Clients & Targets';
                }

                return this.filteredItems;
            },
            async getItems(update = false) {     
                this.isLoading = true;
                await this.$store.dispatch('getPlayers', update);

                this.allItems = cloneDeep(this.$store.state.players);
        
                this.getData();
            },
            getStatusFilterIndex() {
                return this.filter.filters.findIndex(filter => filter.field === 'status' || (filter.filters && filter.filters.some(f => f.field === 'status')));
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onCreateItem() {
                this.toggleCreateDialog();
            },
            onDeleteItem(item) {
                this.confirmIconClass='fa-solid fa-trash-can';
                this.deleteItemId = item.id;
                this.confirmMsg = `Are you sure you would like to mark <span>${item.fullName}</span> as inactive?`;
                this.toggleConfirmDialog();
            },
            onFilterChange(evt) {
                this.filter = evt.filter;
                this.getData();
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            onRowClick(evt) {
                if (!this.$store.state.hasAdminAccess) return;

                this.$router.push(`/PlayerDetail?id=${evt.dataItem.id}`);
            },
            onSearchChange(evt) {
                this.searchWord = evt.value;
                this.getData();
            },
            onSortChange(evt) {
                this.sort = evt.sort;
                this.getData();
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            toggleConfirmDialog() {
                this.showConfirmDialog = !this.showConfirmDialog;
            },
            toggleCreateDialog() {
                this.showCreateDialog = !this.showCreateDialog;
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                    this.errorMsg = '';
                }
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-grid': Grid,
            'k-grid-toolbar': GridToolbar,
            'k-loader': Loader,
            'view-header': viewHeader,
            'k-input': Input,
            'k-button': Button,
            'confirm-dialog': confirmDialog,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'create-player-dialog': createPlayerDialog,
            'k-dropdown': DropDownList
        }
    }
</script>

<style>
    @import '/src/assets/css/pipelineViewContainer.css';
    @import '/src/assets/css/defaultUpsertStyles.css';
    @import '/src/assets/css/playerManagementView.css';
</style>